import { hexKey, hexNeighbors, type Hex } from '../hex/index.js';
import type { TerritoryRules } from '../schemas/data/territory.js';
import type { PvpMode } from '../schemas/maps.js';
import type { SquishyState } from '../schemas/squishies.js';
import { isTradingPost, isWaterTerrain, type Supply } from './reach.js';

// Territory rules (design doc §11, decisions B and C), shared so the server
// decides and the client explains with the same logic. Pure: the server
// passes in the tiles and the clock-based counts (cooldowns, daily caps).

/** What a tile needs to know about itself for these rules (`PublicTile` fits). */
export interface TerritoryTile extends Hex {
  readonly ownerUserId: string | null;
  /** Set on a home base's tiles; those can never be battled for. */
  readonly homeSlot: number | null;
  /** Terrain id: a trading post can never be battled for either (#269). Optional for older callers. */
  readonly terrain?: string;
  /**
   * Set on a Hollowed Community's tiles (#306): won by restoring them, never
   * by battle while grey. Optional for older callers.
   */
  readonly communityId?: string | null;
  /**
   * A village tile with all its pieces back (#306 PR 4): ordinary land under
   * normal capture rules while its village is being restored. The server
   * reads it from `community_tiles.restored_at`, the client from
   * `MapView.communities[].restoredTiles`. Unset reads as grey.
   */
  readonly restored?: boolean;
}

/**
 * Why a player can't battle for a tile, or null if they can (time-based
 * limits, like cooldowns and daily caps, are checked separately):
 * - `home`: a Heart Seed or its ring, never taken (design doc §11).
 * - `trading-post`: a trading post, shared and never owned (#269).
 * - `hollowed`: a grey Hollowed village tile, won by restoring it (#306); a
 *   restored one is ordinary land (PR 4), and a won ring is `home` first.
 * - `water`: a Wild Isle's ocean, river or lake (#621), never land.
 * - `mine`: it's already theirs.
 * - `too-far`: not next to their land (outposts come in Phase 2). With a
 *   Wild Isle's `supply` (#621): not next to land joined to their home (or a
 *   bridge they cross), and not in a road's reach.
 * - `pvp-off`: another player's land, and the map's PvP mode is Off.
 *
 * A Cozy Patch passes no `supply` and has no water: the rules are exactly
 * what they were before Wild Isles.
 */
export type AttackTargetProblem =
  'home' | 'trading-post' | 'hollowed' | 'water' | 'mine' | 'too-far' | 'pvp-off';

export function attackTargetProblem(
  target: TerritoryTile,
  tiles: readonly TerritoryTile[],
  userId: string,
  pvpMode: PvpMode,
  supply?: Supply,
): AttackTargetProblem | null {
  if (target.homeSlot !== null) return 'home';
  if (target.terrain !== undefined && isTradingPost({ terrain: target.terrain }))
    return 'trading-post';
  if ((target.communityId ?? null) !== null && target.restored !== true) return 'hollowed';
  if (target.terrain !== undefined && isWaterTerrain(target.terrain)) return 'water';
  if (target.ownerUserId === userId) return 'mine';
  if (supply) {
    const near =
      supply.reach.has(hexKey(target)) ||
      hexNeighbors(target).some((n) => supply.supplied.has(hexKey(n)));
    if (!near) return 'too-far';
  } else {
    const mine = new Set(tiles.filter((t) => t.ownerUserId === userId).map(hexKey));
    if (!hexNeighbors(target).some((n) => mine.has(hexKey(n)))) return 'too-far';
  }
  if (target.ownerUserId !== null && pvpMode === 'off') return 'pvp-off';
  return null;
}

/** Neutral land is claimed from its guardians; a rival's is challenged. */
export function tileBattleKindFor(target: TerritoryTile): 'tile' | 'rival-tile' {
  return target.ownerUserId === null ? 'tile' : 'rival-tile';
}

/** How many tiles a player can lose to challenges per map-local day (0: challenges are off). */
export function dailyLossCap(rules: TerritoryRules, pvpMode: PvpMode): number {
  return pvpMode === 'off' ? 0 : rules.dailyLossCap[pvpMode];
}

/** A player's land for the Gentle rewards rule: the tiles they own, home rings not counted. */
export function landCount(tiles: readonly TerritoryTile[], userId: string): number {
  return tiles.filter((t) => t.ownerUserId === userId && t.homeSlot === null).length;
}

/**
 * The share (%) of capture rewards a challenge earns (decision B): under
 * Gentle, challenging a player with far less land than you earns less.
 */
export function challengeRewardPercent(
  rules: TerritoryRules,
  pvpMode: PvpMode,
  attackerLand: number,
  defenderLand: number,
): number {
  if (pvpMode !== 'gentle') return 100;
  // Integer maths: defender < attacker × share%.
  const smaller = defenderLand * 100 < attackerLand * rules.gentle.smallerBelowPercent;
  return smaller ? rules.gentle.rewardPercent : 100;
}

/** Where a squishy is stationed: the tile it defends, and who owns that tile now. */
export interface WatchPost {
  readonly tileOwnerUserId: string | null;
}

/**
 * Decision C: a squishy stationed to defend its owner's tile is on watch, so
 * the Hollow Man doesn't count it as exposed (#21 uses this). A post on land
 * that changed hands doesn't count (the squishy went home). The server's SQL
 * twin is territory's `squishyOnWatch` (without the `state` check).
 */
export function isOnWatch(
  squishy: { readonly ownerUserId: string; readonly state: SquishyState },
  post: WatchPost | null,
): boolean {
  return (
    post !== null && squishy.state === 'active' && post.tileOwnerUserId === squishy.ownerUserId
  );
}
export * from './tending.js';
export * from './fences.js';
export * from './reach.js';
export * from './water.js';
export * from './journeys.js';
export * from './watch-level.js';
export * from './roads.js';
export * from './buildable-on.js';
